// Status Flow Rules Configuration
// Defines valid status transitions for different entity types

export const statusFlowRules = {
  // Order Item Status Transitions
  orderItem: {
    // Buy Item Flow
    buy: {
      'pending': ['confirmed', 'cancelled'],
      'confirmed': ['ready_for_shipping', 'cancelled'],
      'ready_for_shipping': ['shipped', 'cancelled'],
      'shipped': ['in_transit', 'delivered'],
      'in_transit': ['delivered'],
      'delivered': ['return_requested', 'reviewed'],
      'return_requested': ['return_approved', 'return_rejected'],
      'return_approved': ['awaiting_return_shipment'],
      'awaiting_return_shipment': ['return_in_transit'],
      'return_in_transit': ['return_delivered_to_merchant'],
      'return_delivered_to_merchant': ['return_processing', 'return_rejected'],
      'return_processing': ['refund_processed', 'return_completed'],
      'refund_processed': ['return_completed'],
      'return_completed': [],
      'return_rejected': [],
      'cancelled': [],
      'reviewed': []
    },
    
    // Rental Item Flow
    rental: {
      'pending': ['confirmed', 'cancelled'],
      'confirmed': ['ready_for_shipping', 'cancelled'],
      'ready_for_shipping': ['shipped', 'cancelled'],
      'shipped': ['in_transit', 'delivered'],
      'in_transit': ['delivered'],
      'delivered': ['active_rental', 'return_requested', 'keep_item_requested'],
      'active_rental': ['return_requested', 'keep_item_requested', 'rental_extended'],
      'rental_extended': ['return_requested', 'keep_item_requested'],
      'return_requested': ['return_approved', 'return_rejected'],
      'return_approved': ['awaiting_return_shipment'],
      'awaiting_return_shipment': ['return_in_transit'],
      'return_in_transit': ['return_delivered_to_merchant'],
      'return_delivered_to_merchant': ['rental_return_processing', 'damaged_on_return'],
      'rental_return_processing': ['rental_return_completed'],
      'rental_return_completed': [],
      'keep_item_requested': ['payment_processing', 'purchase_approved'],
      'payment_processing': ['item_purchased', 'payment_failed'],
      'item_purchased': ['rental_converted_to_purchase'],
      'rental_converted_to_purchase': [],
      'damaged_on_return': ['security_deposit_claimed'],
      'security_deposit_claimed': [],
      'cancelled': []
    }
  },

  // Fulfillment Group Status Transitions
  fulfillmentGroup: {
    active: [
      'processing', 'ready_for_shipping', 'ready_for_pickup', 'shipped_to_customer', 
      'picked_up_by_customer', 'delivery_completed_to_customer',
      'group_return_approved', 'partial_return_approved',
      'group_return_in_transit_to_merchant', 'partial_return_in_transit_to_merchant', 'mixed_return_in_transit_to_merchant',
      'group_return_delivered_to_merchant', 'partial_return_delivered_to_merchant', 'mixed_return_delivered_to_merchant',
      'group_return_processing_by_merchant', 'partial_return_processing_by_merchant', 'mixed_return_processing'
    ],
    processing: [
      'group_return_requested', 'partial_return_requested', 'mixed_return_scenario',
      'awaiting_group_return_shipment', 'awaiting_partial_return_shipment', 'awaiting_mixed_return_shipment'
    ],
    completed: [
      'return_completed_for_group', 'partial_return_completed', 'fulfillment_partially_returned', 'return_completed_mixed', 'fulfillment_partially_completed', 'completed'
    ],
    cancelled: ['cancelled', 'group_return_rejected'],
    error: ['delivery_failed']
  },

  // Order Status Transitions
  order: {
    'pending': ['paid', 'cancelled'],
    'paid': ['processing', 'cancelled'],
    'processing': ['partially_shipped', 'shipped', 'completed', 'cancelled'],
    'partially_shipped': ['shipped', 'completed'],
    'shipped': ['partially_delivered', 'delivered', 'completed'],
    'partially_delivered': ['delivered', 'completed'],
    'delivered': ['completed', 'partially_returned'],
    'partially_returned': ['completed', 'fully_returned'],
    'fully_returned': ['completed'],
    'completed': [],
    'cancelled': []
  },

  // Request Status Transitions
  request: {
    'pending': ['pending_merchant_review', 'cancelled'],
    'pending_merchant_review': ['approved', 'rejected', 'pending_customer_action'],
    'pending_customer_action': ['pending_merchant_review', 'cancelled'],
    'approved': ['in_progress', 'completed'],
    'in_progress': ['completed', 'failed'],
    'completed': [],
    'rejected': [],
    'cancelled': [],
    'failed': ['pending_merchant_review'] // Allow retry
  }
};

// Helper function to check if a status transition is valid
export const isValidTransition = (entityType, itemType, currentStatus, newStatus) => {
  // For order items, check item type specific rules
  if (entityType === 'orderItem' && itemType) {
    const itemRules = statusFlowRules[entityType]?.[itemType];
    if (itemRules) {
      return itemRules[currentStatus]?.includes(newStatus) || false;
    }
  }
  
  // For other entities, use generic rules
  const entityRules = statusFlowRules[entityType];
  if (entityRules && typeof entityRules[currentStatus] !== 'undefined') {
    return entityRules[currentStatus].includes(newStatus);
  }
  
  return false;
};

// Helper function to get all valid next statuses
export const getValidNextStatuses = (entityType, itemType, currentStatus) => {
  // For order items, check item type specific rules
  if (entityType === 'orderItem' && itemType) {
    const itemRules = statusFlowRules[entityType]?.[itemType];
    if (itemRules) {
      return itemRules[currentStatus] || [];
    }
  }
  
  // For other entities, use generic rules
  const entityRules = statusFlowRules[entityType];
  if (entityRules && typeof entityRules[currentStatus] !== 'undefined') {
    return entityRules[currentStatus];
  }
  
  return [];
};

// Helper function to check if a status is terminal (no further transitions)
export const isTerminalStatus = (entityType, itemType, status) => {
  const validNextStatuses = getValidNextStatuses(entityType, itemType, status);
  return validNextStatuses.length === 0;
};

// Status categories for UI grouping and styling
export const statusCategories = {
  orderItem: {
    active: ['pending', 'confirmed', 'ready_for_shipping', 'shipped', 'in_transit', 'delivered', 'active_rental'],
    processing: ['return_requested', 'return_approved', 'awaiting_return_shipment', 'return_in_transit', 'return_processing', 'rental_return_processing', 'payment_processing'],
    completed: ['return_completed', 'rental_return_completed', 'rental_converted_to_purchase', 'reviewed'],
    cancelled: ['canceled', 'return_rejected'],
    problematic: ['damaged_on_return', 'payment_failed', 'security_deposit_claimed']
  },
  
  fulfillmentGroup: {
    active: ['processing', 'ready_for_shipping', 'ready_for_pickup', 'shipped_to_customer', 'picked_up_by_customer', 'delivery_completed_to_customer', 'group_return_approved', 'partial_return_approved', 'group_return_in_transit_to_merchant', 'partial_return_in_transit_to_merchant', 'mixed_return_in_transit_to_merchant', 'group_return_delivered_to_merchant', 'partial_return_delivered_to_merchant', 'mixed_return_delivered_to_merchant', 'group_return_processing_by_merchant', 'partial_return_processing_by_merchant', 'mixed_return_processing'],
    processing: ['group_return_requested', 'partial_return_requested', 'mixed_return_scenario', 'awaiting_group_return_shipment', 'awaiting_partial_return_shipment', 'awaiting_mixed_return_shipment'],
    completed: ['return_completed_for_group', 'group_refund_processed', 'rental_group_return_processed'],
    cancelled: ['canceled', 'group_return_rejected']
  },
  
  order: {
    active: ['pending', 'paid', 'processing', 'partially_shipped', 'shipped', 'partially_delivered', 'delivered'],
    processing: ['partially_returned'],
    completed: ['completed', 'fully_returned'],
    cancelled: ['canceled']
  },
  
  request: {
    active: ['pending', 'pending_merchant_review', 'pending_customer_action', 'approved', 'in_progress'],
    completed: ['completed'],
    cancelled: ['canceled', 'rejected', 'failed']
  }
};

// Helper function to get status category
export const getStatusCategory = (entityType, status) => {
  if (status && typeof status === 'string' && status.toLowerCase().includes('return')) {
    return 'processing';
  }
  const categories = statusCategories[entityType];
  if (!categories) return 'unknown';
  
  for (const [category, statuses] of Object.entries(categories)) {
    if (statuses.includes(status)) {
      return category;
    }
  }
  
  return 'unknown';
};

// Status display configurations
export const statusDisplayConfig = {
  // Colors for different status categories
  colors: {
    active: {
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      border: 'border-blue-200'
    },
    processing: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-800', 
      border: 'border-yellow-200'
    },
    completed: {
      bg: 'bg-green-50',
      text: 'text-green-800',
      border: 'border-green-200'
    },
    cancelled: {
      bg: 'bg-red-50',
      text: 'text-red-800',
      border: 'border-red-200'
    },
    problematic: {
      bg: 'bg-orange-50',
      text: 'text-orange-800',
      border: 'border-orange-200'
    }
  },
  
  // Human-readable status labels
  labels: {
    // Order Item Labels
    'pending': 'Pending',
    'confirmed': 'Confirmed',
    'ready_for_shipping': 'Ready for Shipping',
    'shipped': 'Shipped',
    'in_transit': 'In Transit',
    'delivered': 'Delivered',
    'active_rental': 'Active Rental',
    'return_requested': 'Return Requested',
    'return_approved': 'Return Approved',
    'awaiting_return_shipment': 'Awaiting Return Shipment',
    'return_in_transit': 'Return in Transit',
    'return_delivered_to_merchant': 'Return Delivered',
    'return_processing': 'Processing Return',
    'rental_return_processing': 'Processing Rental Return',
    'return_completed': 'Return Completed',
    'rental_return_completed': 'Rental Return Completed',
    'keep_item_requested': 'Purchase Requested',
    'payment_processing': 'Processing Payment',
    'item_purchased': 'Item Purchased',
    'rental_converted_to_purchase': 'Converted to Purchase',
    'damaged_on_return': 'Damaged on Return',
    'security_deposit_claimed': 'Security Deposit Claimed',
    'cancelled': 'Cancelled',
    'canceled': 'Canceled',
    'reviewed': 'Reviewed',
    
    // Fulfillment Group Labels
    'processing': 'Processing',
    'ready_for_pickup': 'Ready for Pickup',
    'shipped_to_customer': 'Shipped to Customer',
    'picked_up_by_customer': 'Picked Up by Customer',
    'delivery_completed_to_customer': 'Delivered to Customer',
    'delivery_failed': 'Delivery Failed',
    'group_return_requested': 'Group Return Requested',
    'partial_return_requested': 'Partial Return Requested',
    'mixed_return_scenario': 'Mixed Return Scenario',
    'group_return_approved': 'Group Return Approved',
    'partial_return_approved': 'Partial Return Approved',
    'group_return_rejected': 'Group Return Rejected',
    'awaiting_group_return_shipment': 'Awaiting Group Return',
    'awaiting_partial_return_shipment': 'Awaiting Partial Return',
    'awaiting_mixed_return_shipment': 'Awaiting Mixed Return',
    'group_return_in_transit_to_merchant': 'Group Return In Transit',
    'partial_return_in_transit_to_merchant': 'Partial Return In Transit',
    'mixed_return_in_transit_to_merchant': 'Mixed Return In Transit',
    'group_return_delivered_to_merchant': 'Group Return Delivered',
    'partial_return_delivered_to_merchant': 'Partial Return Delivered',
    'mixed_return_delivered_to_merchant': 'Mixed Return Delivered',
    'group_return_processing_by_merchant': 'Processing Group Return',
    'partial_return_processing_by_merchant': 'Processing Partial Return',
    'mixed_return_processing': 'Processing Mixed Return',
    'return_completed_for_group': 'Group Return Completed',
    'partial_return_completed': 'Partial Return Completed',
    'fulfillment_partially_returned': 'Partially Returned',
    'return_completed_mixed': 'Mixed Return Completed',
    'fulfillment_partially_completed': 'Partially Completed',
    
    // Order Labels
    'paid': 'Paid',
    'partially_shipped': 'Partially Shipped',
    'partially_delivered': 'Partially Delivered',
    'partially_returned': 'Partially Returned',
    'fully_returned': 'Fully Returned',
    'completed': 'Completed',
    
    // Request Labels
    'pending_merchant_review': 'Pending Merchant Review',
    'pending_customer_action': 'Pending Customer Action',
    'approved': 'Approved',
    'in_progress': 'In Progress',
    'rejected': 'Rejected',
    'failed': 'Failed'
  }
};

// Helper function to get status display configuration
export const getStatusDisplay = (entityType, status) => {
  const category = getStatusCategory(entityType, status);
  const colors = statusDisplayConfig.colors[category] || statusDisplayConfig.colors.active;
  const autoLabel = status
    .replace(/_/g, ' ') // underscores to spaces
    .replace(/\b\w/g, c => c.toUpperCase());
  const label = statusDisplayConfig.labels[status] || autoLabel;
  
  return {
    label,
    category,
    ...colors
  };
};

export default statusFlowRules; 